// Choix d'une source documentaire pour étayer une compatibilité : extraits de la bibliothèque validée ou pièce du dossier (page précise).
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, FileText, Search } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Badge, Bouton, Champ, Chargement, Dialogue, EtatErreur, EtatVide, Surligne } from '../../components/ui.tsx';
import type { SourceLiaison } from './modele.ts';

interface Reponse {
  pieces: { versionId: string; titre: string; version: string; pages: number | null; mime: string; statut: string }[];
  extraits: { versionId: string; page: number; titre: string; version: string; fabricant: string | null; localisation: string | null; extrait: string; portee: 'bibliotheque' | 'dossier' }[];
}

export function ChoixSource({ dossierId, ouvert, fermer, choisir, suggestion }:
  { dossierId: string; ouvert: boolean; fermer: () => void; choisir: (s: SourceLiaison) => void; suggestion?: string }) {
  const [saisie, setSaisie] = useState(suggestion ?? '');
  const [q, setQ] = useState(suggestion ?? '');
  const [pages, setPages] = useState<Record<string, string>>({});
  useEffect(() => { if (ouvert) { setSaisie(suggestion ?? ''); setQ(suggestion ?? ''); } }, [ouvert, suggestion]);
  useEffect(() => { const t = window.setTimeout(() => setQ(saisie.trim()), 350); return () => window.clearTimeout(t); }, [saisie]);
  const r = useQuery({
    queryKey: ['conception-sources', dossierId, q], enabled: ouvert,
    queryFn: () => api.get<Reponse>(`/api/dossiers/${dossierId}/conception/sources?q=${encodeURIComponent(q)}`),
  });

  return (
    <Dialogue titre="Étayer la compatibilité" ouvert={ouvert} fermer={fermer} large>
      <div className="pile">
        <p className="petit secondaire">
          Citez la page qui prouve la compatibilité : une version validée de la bibliothèque documentaire, ou une pièce de ce dossier.
          Sans source, la liaison reste « à vérifier ».
        </p>
        <Champ libelle="Rechercher dans la documentation" type="search" value={saisie} onChange={(e) => setSaisie(e.target.value)}
          placeholder="Référence, passerelle, protocole…" aide="Recherche par référence exacte, puis dans le texte des documents validés." />
        {r.isLoading && <Chargement lignes={3} libelle="Recherche des sources…" />}
        {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
        {r.data && (
          <>
            {q && (
              <section className="pile" style={{ gap: 8 }} aria-label="Extraits trouvés">
                <h3 className="petit" style={{ fontWeight: 600 }}>Extraits trouvés</h3>
                {!r.data.extraits.length && <EtatVide icone={<Search />} titre="Aucun extrait trouvé" texte="Essayez la référence exacte du produit ou citez une pièce du dossier ci-dessous." />}
                <ul className="cch-sources">
                  {r.data.extraits.map((x, i) => (
                    <li key={`${x.versionId}-${x.page}-${i}`} className="cch-source">
                      <div className="pile" style={{ gap: 4, minWidth: 0, flex: 1 }}>
                        <span className="rangee" style={{ gap: 8 }}>
                          <strong>{x.titre}</strong>
                          <span className="tres-petit discret">p. {x.page}{x.localisation ? `, ${x.localisation}` : ''} · version {x.version}</span>
                          <Badge ton={x.portee === 'bibliotheque' ? 'info' : 'neutre'} icone={x.portee === 'bibliotheque' ? <BookOpen aria-hidden /> : <FileText aria-hidden />}>
                            {x.portee === 'bibliotheque' ? 'Bibliothèque' : 'Pièce du dossier'}
                          </Badge>
                        </span>
                        <span className="petit secondaire"><Surligne texte={x.extrait} /></span>
                      </div>
                      <Bouton compact onClick={() => { choisir({ versionId: x.versionId, page: x.page }); fermer(); }}>Citer cette page</Bouton>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <section className="pile" style={{ gap: 8 }} aria-label="Pièces du dossier">
              <h3 className="petit" style={{ fontWeight: 600 }}>Pièces du dossier</h3>
              {!r.data.pieces.length && <p className="petit discret">Aucune pièce dans ce dossier.</p>}
              <ul className="cch-sources">
                {r.data.pieces.map((p) => {
                  const page = pages[p.versionId] ?? '1';
                  const n = Number(page);
                  const ok = Number.isInteger(n) && n >= 1 && (!p.pages || n <= p.pages);
                  return (
                    <li key={p.versionId} className="cch-source">
                      <span className="pile" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                        <strong>{p.titre}</strong>
                        <span className="tres-petit discret">version {p.version}{p.pages ? ` · ${p.pages} page(s)` : ''}</span>
                      </span>
                      <span className="rangee" style={{ gap: 8, flexWrap: 'nowrap' }}>
                        <label className="tres-petit" htmlFor={`page-${p.versionId}`}>Page</label>
                        <input id={`page-${p.versionId}`} className="saisie cch-saisie-courte" inputMode="numeric" value={page}
                          onChange={(e) => setPages((x) => ({ ...x, [p.versionId]: e.target.value }))} aria-invalid={!ok || undefined} />
                        <Bouton compact disabled={!ok} onClick={() => { choisir({ versionId: p.versionId, page: n }); fermer(); }}>Citer</Bouton>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          </>
        )}
      </div>
    </Dialogue>
  );
}
